import { api } from './api';

// posto de combustível: leitura do encerrante, aferição e recebimento de
// diesel (api /fuel-readings)

// abertura = leitura das 7h; fechamento = das 17h (sábado 11h)
export type FuelSlot = 'abertura' | 'fechamento';

export interface FuelPump {
  id: string;
  code: string;
  name: string;
  fuel_type: string;
  branch: string | null;
  erp_product_code: number | null;
  expected_times: string;
  expected_weekdays: string;
  saturday_times: string;
  max_jump_liters: number;
  active: boolean;
  display_order: number;
  last_reading: number | null;
  last_read_at: string | null;
  last_slot: FuelSlot | null;
  last_read_by: string | null;
  last_calibration_at: string | null;
  last_calibration_approved: boolean | null;
}

export type FuelPumpInput = Partial<
  Pick<
    FuelPump,
    | 'code'
    | 'name'
    | 'fuel_type'
    | 'branch'
    | 'erp_product_code'
    | 'expected_times'
    | 'expected_weekdays'
    | 'saturday_times'
    | 'max_jump_liters'
    | 'active'
    | 'display_order'
  >
> & {
  // obrigatório quando muda o "active" (vai para o histórico da bomba)
  status_reason?: string;
};

// cada vez que a bomba foi ativada/desativada
export interface FuelPumpStatusChange {
  id: string;
  pump_id: string;
  active: boolean;
  reason: string;
  access_name: string | null;
  created_at: string;
}

export interface FuelReading {
  id: string;
  pump_id: string;
  pump_name: string;
  slot: FuelSlot;
  reading: number | null;
  previous_reading: number | null;
  liters: number | null;
  photo: string | null;
  photo_url: string | null;
  // foto anexada depois do lançamento (null: veio junto ou ainda não tem)
  photo_attached_at: string | null;
  photo_attached_by_name: string | null;
  read_at: string;
  access_name: string | null;
  source: 'samasc' | 'desktop' | 'liberado_ti';
  high_jump_confirmed: boolean;
  notes: string | null;
  canceled_at: string | null;
  canceled_by_name: string | null;
  cancel_reason: string | null;
  // número corrigido depois (null: nunca foi corrigido). original_reading é o
  // número lançado; os outros são da última correção
  original_reading: number | null;
  edited_at: string | null;
  edited_by_name: string | null;
  edit_reason: string | null;
  // só na resposta do lançamento: saiu combustível entre o fechamento e a abertura
  overnight_liters?: number | null;
}

export interface FuelCalibration {
  id: string;
  pump_id: string;
  pump_name: string;
  volume_liters: number;
  deviation_ml: number;
  approved: boolean;
  photo: string | null;
  photo_url: string | null;
  // foto anexada depois do lançamento (null: veio junto ou ainda não tem)
  photo_attached_at: string | null;
  photo_attached_by_name: string | null;
  notes: string | null;
  calibrated_at: string;
  access_name: string | null;
  canceled_at: string | null;
  canceled_by_name: string | null;
  cancel_reason: string | null;
}

// descarga de diesel anotada no posto (nf do fornecedor)
export interface FuelReceipt {
  id: string;
  document: string;
  liters: number;
  received_on: string; // yyyy-mm-dd
  photo: string | null;
  photo_url: string | null;
  // foto anexada depois do lançamento (null: veio junto ou ainda não tem)
  photo_attached_at: string | null;
  photo_attached_by_name: string | null;
  notes: string | null;
  access_name: string | null;
  created_at: string;
  canceled_at: string | null;
  canceled_by_name: string | null;
  cancel_reason: string | null;
}

// a descarga conferida com a entrada da nf no gescooper
export interface FuelReceiptCheck {
  id: string;
  document: string;
  liters: number;
  received_on: string;
  erp_day: string | null; // dia da entrada no gescooper (null = ainda não deu entrada)
  erp_liters: number | null;
  late_days: number | null;
}

// uma linha da folha "controle diesel" para uma bomba
export interface FuelSummaryPumpDay {
  pump_id: string;
  opening: number | null; // leitura 7h
  opening_at: string | null;
  closing: number | null; // leitura 17h
  closing_at: string | null;
  calibration_liters: number; // aferição bomba (l)
  pumped: number | null; // 17h - 7h
  net: number | null; // total bombeado dia (17h - 7h - aferição)
  overnight: number | null; // abertura - fechamento anterior
  expected: FuelSlot[];
  missing: FuelSlot[];
}

export interface FuelSummaryDay {
  day: string;
  weekday: number;
  pumps: FuelSummaryPumpDay[];
  pumped_liters: number;
  calibration_liters: number;
  net_liters: number;
  complete: boolean;
  closed: boolean; // sem leitura e sem venda: feriado ou posto fechado
  in_liters: number; // entrada nf (anotada no posto)
  in_documents: string[]; // nº nf (anotada no posto)
  receipts: FuelReceiptCheck[];
  erp_out: number | null; // saídas no sistema
  erp_in: number | null; // entradas lançadas no gescooper no dia
  erp_in_documents: string[];
  difference: number | null; // total bombeado - saídas no sistema
  large_sales: { document: string; liters: number }[]; // nf de venda > 500 l
  expected: number;
  readings: number;
  missing: number;
  occurrences: string[];
}

export interface FuelSummary {
  from: string;
  to: string;
  pumps: (FuelPump & { calibration_overdue: boolean })[];
  days: FuelSummaryDay[];
  receipts: FuelReceiptCheck[]; // descargas do período
  pending_receipts: FuelReceiptCheck[]; // descargas sem entrada no gescooper
  erp: { available: boolean; error: string | null };
}

export interface FuelAccess {
  canCreate: boolean;
  canView: boolean;
  canRelease: boolean;
  isAdmin: boolean;
}

export function fuelErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;

  return typeof message === 'string' && message.trim() ? message : fallback;
}

// código fixo do erro (ex.: "RELEASE_DUPLICATE"), quando a api manda
export function fuelErrorCode(err: unknown): string | undefined {
  const code = (err as { response?: { data?: { code?: unknown } } })?.response
    ?.data?.code;

  return typeof code === 'string' ? code : undefined;
}

export function fuelErrorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

// a foto vem com caminho relativo (/uploads/...): completa com o endereço da api
export function fullPhotoUrl(url: string | null): string | null {
  if (!url) {
    return null;
  }

  if (/^https?:\/\//.test(url)) {
    return url;
  }

  return `${(api.defaults.baseURL || '').replace(/\/$/, '')}${url}`;
}

interface PeriodParams {
  from?: string;
  to?: string;
  pump_id?: string;
  limit?: number;
}

// o que dá para anexar foto depois
export type FuelRecordKind = 'reading' | 'calibration' | 'receipt';

const ATTACH_PATHS: Record<FuelRecordKind, string> = {
  reading: 'readings',
  calibration: 'calibrations',
  receipt: 'receipts',
};

export const fuelReadingsService = {
  async me(): Promise<FuelAccess> {
    const response = await api.get<FuelAccess>('/fuel-readings/me');

    return response.data;
  },

  async pumps(): Promise<FuelPump[]> {
    const response = await api.get<FuelPump[]>('/fuel-readings/pumps');

    return Array.isArray(response.data) ? response.data : [];
  },

  async savePump(data: FuelPumpInput, id?: string): Promise<FuelPump> {
    const response = id
      ? await api.put<FuelPump>(`/fuel-readings/pumps/${id}`, data)
      : await api.post<FuelPump>('/fuel-readings/pumps', data);

    return response.data;
  },

  async createReading(data: {
    pump_id: string;
    slot: FuelSlot;
    reading: string;
    photo: Blob | null;
    notes?: string;
    confirm_high_jump?: boolean;
  }): Promise<FuelReading> {
    const form = new FormData();

    form.append('pump_id', data.pump_id);
    form.append('slot', data.slot);
    form.append('reading', data.reading);
    if (data.notes) {
      form.append('notes', data.notes);
    }
    if (data.confirm_high_jump) {
      form.append('confirm_high_jump', 'true');
    }
    if (data.photo) {
      form.append('photo', data.photo, 'totalizador.jpg');
    }

    const response = await api.post<FuelReading>(
      '/fuel-readings/readings',
      form,
    );

    return response.data;
  },

  async readings(params: PeriodParams): Promise<FuelReading[]> {
    const response = await api.get<FuelReading[]>('/fuel-readings/readings', {
      params,
    });

    return Array.isArray(response.data) ? response.data : [];
  },

  async cancelReading(id: string, reason: string): Promise<FuelReading> {
    const response = await api.post<FuelReading>(
      `/fuel-readings/readings/${id}/cancel`,
      { reason },
    );

    return response.data;
  },

  // supervisor ou ti: troca o número lançado errado (o antigo fica no histórico)
  async editReading(
    id: string,
    data: { reading: string; reason: string; confirm_high_jump?: boolean },
  ): Promise<FuelReading> {
    const response = await api.put<FuelReading>(
      `/fuel-readings/readings/${id}`,
      data,
    );

    return response.data;
  },

  async createCalibration(data: {
    pump_id: string;
    deviation_ml: string;
    photo: Blob | null;
    notes?: string;
  }): Promise<FuelCalibration> {
    const form = new FormData();

    form.append('pump_id', data.pump_id);
    form.append('deviation_ml', data.deviation_ml);
    if (data.notes) {
      form.append('notes', data.notes);
    }
    if (data.photo) {
      form.append('photo', data.photo, 'afericao.jpg');
    }

    const response = await api.post<FuelCalibration>(
      '/fuel-readings/calibrations',
      form,
    );

    return response.data;
  },

  async calibrations(params: PeriodParams): Promise<FuelCalibration[]> {
    const response = await api.get<FuelCalibration[]>(
      '/fuel-readings/calibrations',
      { params },
    );

    return Array.isArray(response.data) ? response.data : [];
  },

  async cancelCalibration(
    id: string,
    reason: string,
  ): Promise<FuelCalibration> {
    const response = await api.post<FuelCalibration>(
      `/fuel-readings/calibrations/${id}/cancel`,
      { reason },
    );

    return response.data;
  },

  async createReceipt(data: {
    document: string;
    liters: string;
    received_on: string;
    photo: Blob | null;
    notes?: string;
  }): Promise<FuelReceipt> {
    const form = new FormData();

    form.append('document', data.document);
    form.append('liters', data.liters);
    form.append('received_on', data.received_on);
    if (data.notes) {
      form.append('notes', data.notes);
    }
    if (data.photo) {
      form.append('photo', data.photo, 'nota-fiscal.jpg');
    }

    const response = await api.post<FuelReceipt>(
      '/fuel-readings/receipts',
      form,
    );

    return response.data;
  },

  async receipts(params: PeriodParams): Promise<FuelReceipt[]> {
    const response = await api.get<FuelReceipt[]>('/fuel-readings/receipts', {
      params,
    });

    return Array.isArray(response.data) ? response.data : [];
  },

  async cancelReceipt(id: string, reason: string): Promise<FuelReceipt> {
    const response = await api.post<FuelReceipt>(
      `/fuel-readings/receipts/${id}/cancel`,
      { reason },
    );

    return response.data;
  },

  // foto anexada depois (leitura do pc do posto, aferição ou nota sem foto)
  async attachPhoto(
    kind: FuelRecordKind,
    id: string,
    photo: Blob,
  ): Promise<FuelReading | FuelCalibration | FuelReceipt> {
    const form = new FormData();

    form.append('photo', photo, 'foto.jpg');

    const response = await api.post<
      FuelReading | FuelCalibration | FuelReceipt
    >(`/fuel-readings/${ATTACH_PATHS[kind]}/${id}/photo`, form);

    return response.data;
  },

  async summary(from: string, to: string): Promise<FuelSummary> {
    const response = await api.get<FuelSummary>('/fuel-readings/summary', {
      params: { from, to },
    });

    return response.data;
  },

  async pumpStatusHistory(id: string): Promise<FuelPumpStatusChange[]> {
    const response = await api.get<FuelPumpStatusChange[]>(
      `/fuel-readings/pumps/${id}/status-history`,
    );

    return Array.isArray(response.data) ? response.data : [];
  },

  // libera o turno de hoje sem leitura (supervisor ou ti), com motivo. anula
  // pelo cancelReading, como uma leitura.
  async release(data: {
    pump_id: string;
    slot: FuelSlot;
    reason: string;
  }): Promise<FuelReading> {
    const response = await api.post<FuelReading>(
      '/fuel-readings/release',
      data,
    );

    return response.data;
  },
};
